import React, { useCallback, useRef, useMemo } from "react";

import classNames from "classnames";

import { Tooltip, useTooltip, useLevelsContext } from "@braze/beacon-core";

import { StackOrder } from "@lib/canvasStackOrder";

import { useBrazeDashboardConfig } from "@services/brazeDashboardConfig";
import { useAgenticAiContext } from "@services/useAgenticAiContext";
import useI18nNamespace from "@services/useI18nNamespace";
import { useInEditorDomain } from "@services/useInEditorDomain";

import { useInlineAgenticChat } from "../../hooks/useInlineAgenticChat";
import { BrazeOperatorWhiteSparkleIcon } from "../../assets";
import type { ToggleBrazeOperatorMessage } from "../../hooks";
import { StyledBrazeOperatorLaunchButton } from "./BrazeOperatorLaunchButton.style";
import type { BrazeOperatorLaunchButtonProps } from "./types";

export const BrazeOperatorLaunchButton = ({
  isInIframe = false,
  tooltipPlacement = "bottom-end",
  ...props
}: BrazeOperatorLaunchButtonProps) => {
  const { t } = useI18nNamespace("agentic-ai-chat-ui");
  const { toggleOpenChat } = useAgenticAiContext();
  const ref = useRef<HTMLButtonElement>(null);
  const dashboardUrl = useBrazeDashboardConfig("dashboardUrl");
  const inlineEnabled = useInlineAgenticChat();

  const [tooltipState, targetRef] = useTooltip<HTMLButtonElement>(ref, false);

  const handleClick = useCallback(() => {
    if (isInIframe) {
      // Send a message to the parent window to toggle the operator
      const message: ToggleBrazeOperatorMessage = { type: "toggle-braze-operator" };
      window.parent.postMessage(message, dashboardUrl);
    } else {
      toggleOpenChat();
    }
  }, [dashboardUrl, isInIframe, toggleOpenChat]);

  // editor domain iframe z-index logic. (the fullscreen editor modalss still have high z-indexes even within their iframe)
  const baseZIndexForIframeFullscreenMode = useLevelsContext(1, StackOrder.OVERLAY_CONTENT_ABOVE_POPOVER);
  const inEditorDomain = useInEditorDomain();

  // Hardcode a z-index in editor domains. Use default z-index for non-editor domains.
  const tooltipZIndex = useMemo(() => {
    return inEditorDomain ? baseZIndexForIframeFullscreenMode + 1 : undefined;
  }, [baseZIndexForIframeFullscreenMode, inEditorDomain]);

  return (
    <>
      <StyledBrazeOperatorLaunchButton
        {...props}
        ref={ref}
        onClick={handleClick}
        aria-label={t("braze-operator-launch-button-tooltip", "BrazeAI Operator™")}
        className={classNames("db-braze-operator-launch-button", {
          "dashboard-drawer-ignore-click-outside": inlineEnabled,
        })}
      >
        <BrazeOperatorWhiteSparkleIcon />
      </StyledBrazeOperatorLaunchButton>
      <Tooltip {...tooltipState} targetRef={targetRef} placement={tooltipPlacement} zIndex={tooltipZIndex}>
        {t("braze-operator-launch-button-tooltip", "BrazeAI Operator™")}
      </Tooltip>
    </>
  );
};

BrazeOperatorLaunchButton.displayName = "BrazeOperatorLaunchButton";
